import { callableClient } from '../platform/callables';
import type { ApprovedSubmissionLocation } from '../types';

export const TASK_COMMAND_VERSION = 1 as const;

export type TaskCommandAction =
  | 'claim'
  | 'abandon'
  | 'submit'
  | 'return'
  | 'reopen'
  | 'assignReview'
  | 'claimReview'
  | 'releaseReview'
  | 'requestChanges'
  | 'escalateReview'
  | 'approve';

export interface TaskCommandRequest {
  version: typeof TASK_COMMAND_VERSION;
  taskId: string;
  campaignId?: string;
  idempotencyKey: string;
  bypassClaimingPaused?: boolean;
  expectedReviewRound?: number;
  expectedReviewerId?: string | null;
  reviewerId?: string;
  comment?: string;
  note?: string;
  approvedSubmission?: ApprovedSubmissionLocation;
  result?: Record<string, unknown>;
}

export interface TaskCommandResponse {
  version: typeof TASK_COMMAND_VERSION;
  action: TaskCommandAction | 'assign' | 'claim' | 'release' | 'requestChanges' | 'escalate' | 'approve';
  taskId: string;
  campaignId: string;
  status: 'claimed' | 'available' | 'in_review' | 'in_progress' | 'changes_requested' | 'approved';
  idempotent: boolean;
  reviewRound?: number;
}

/**
 * Error returned by a task command. `commandCode` is the stable domain code
 * from the Function details payload; `code` remains the Firebase callable
 * code when one is available so callers can distinguish network failures.
 */
export class TaskCommandError extends Error {
  readonly commandCode: string;

  readonly details: Record<string, unknown>;

  readonly retryable: boolean;

  readonly code: string;

  constructor(
    message: string,
    commandCode: string,
    details: Record<string, unknown> = {},
    code = commandCode,
    retryable = false,
  ) {
    super(message);
    this.name = 'TaskCommandError';
    this.commandCode = commandCode;
    this.details = details;
    this.code = code;
    this.retryable = retryable;
  }
}

function commandCodeFrom(error: unknown): string {
  if (error && typeof error === 'object') {
    const details = (error as { details?: unknown }).details;
    if (details && typeof details === 'object' && !Array.isArray(details)) {
      const code = (details as { code?: unknown }).code;
      if (typeof code === 'string' && code) return code;
    }
    const code = (error as { code?: unknown }).code;
    if (typeof code === 'string' && code) return code.replace(/^functions\//, '');
  }
  return 'internal';
}

function detailsFrom(error: unknown): Record<string, unknown> {
  if (!error || typeof error !== 'object') return {};
  const details = (error as { details?: unknown }).details;
  return details && typeof details === 'object' && !Array.isArray(details)
    ? details as Record<string, unknown>
    : {};
}

function messageFrom(error: unknown, commandCode: string): string {
  if (error && typeof error === 'object') {
    const message = (error as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) return message;
  }
  if (commandCode === 'unavailable' || commandCode === 'deadline-exceeded') {
    return 'The task service is temporarily unavailable. Check your connection and try again.';
  }
  return 'The task command could not be completed. Please try again.';
}

function isRetryableCode(code: string): boolean {
  return code === 'unavailable'
    || code === 'deadline-exceeded'
    || code === 'network-request-failed';
}

function normalizeError(error: unknown): TaskCommandError {
  if (error instanceof TaskCommandError) return error;
  const commandCode = commandCodeFrom(error);
  const rawCode = error && typeof error === 'object'
    ? (error as { code?: unknown }).code
    : undefined;
  const callableCode = typeof rawCode === 'string' ? rawCode : commandCode;
  return new TaskCommandError(
    messageFrom(error, commandCode),
    commandCode,
    detailsFrom(error),
    callableCode,
    isRetryableCode(commandCode) || isRetryableCode(callableCode.replace(/^functions\//, '')),
  );
}

/** Generate a short key that stays within the callable request contract. */
export function newTaskCommandIdempotencyKey(
  action: TaskCommandAction,
  taskId: string,
): string {
  const random = typeof globalThis.crypto?.randomUUID === 'function'
    ? globalThis.crypto.randomUUID()
    : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  return `${action}-${taskId}-${random}`.slice(0, 200);
}

export function isTaskCommandError(value: unknown): value is TaskCommandError {
  return value instanceof TaskCommandError;
}

/** Keep command diagnostics useful without logging results, comments, or validation text. */
export function taskCommandErrorMetadata(error: unknown): Record<string, unknown> {
  if (!isTaskCommandError(error)) return {};
  const serverState: Record<string, unknown> = {};
  for (const key of [
    'taskId', 'status', 'action', 'reviewRound', 'expectedReviewRound',
    'reviewerId', 'expectedReviewerId',
  ]) {
    const value = error.details[key];
    if (value === null || typeof value === 'string' || typeof value === 'number') {
      serverState[key] = value;
    }
  }
  return {
    commandCode: error.commandCode,
    callableCode: error.code,
    retryable: error.retryable,
    serverState,
  };
}

/**
 * Invoke a command with one same-key retry for transport failures. Reusing the
 * idempotency key makes an ambiguous first response safe to replay.
 */
export async function invokeTaskCommand(
  action: TaskCommandAction,
  request: TaskCommandRequest,
): Promise<TaskCommandResponse> {
  const functionName = {
    claim: 'claimTaskV1',
    abandon: 'abandonTaskV1',
    submit: 'submitTaskV1',
    return: 'returnTaskV1',
    reopen: 'reopenTaskV1',
    assignReview: 'assignReviewV1',
    claimReview: 'claimReviewV1',
    releaseReview: 'releaseReviewV1',
    requestChanges: 'requestChangesV1',
    escalateReview: 'escalateReviewV1',
    approve: 'approveTaskV1',
  }[action];
  let retried = false;
  while (true) {
    try {
      return await callableClient.invoke<TaskCommandRequest, TaskCommandResponse>(
        functionName,
        request,
      );
    } catch (error) {
      const normalized = normalizeError(error);
      if (!retried && normalized.retryable) {
        retried = true;
        continue;
      }
      throw normalized;
    }
  }
}
